import React, { FC } from 'react';
import cn from 'clsx';
import { css } from 'linaria';

import Button from '../Button';
import CardFavoriteButton from '../CardsGrid/CardFavoriteButton';
import AppLink from '../AppLink';
import AuthenticatedAppLink from '../AuthenticatedAppLink';

import QuestionIcon from '../Icons/Question';
import PlayIcon from '../Icons/Play';
import NoAdsIcon from '../Icons/NoAds';
import PaletteIcon from '../Icons/Palette';
import BlocksIcon from '../Icons/Blocks';
import TrackingIcon from '../Icons/Tracking';
import ShareIcon from '../Icons/Share';
import PencilEditIcon from '../Icons/PencilEdit';
import PreviewMobileIcon from '../Icons/PreviewMobile';
import EnvelopeIcon from '../Icons/Envelope';

import colors from '../../utils/colorConstants';
import GuestCheckInIcon from '../Icons/GuestCheckIn';

const CustomizeButtonStyles = css`
  transition: background-color 0.2s ease-in-out;

  &:hover {
    background-color: #00479e;
  }
`;

interface TitleAndFavoriteProps {
  className?: string;
  name: string;
  id?: string;
  isFavorite?: boolean;
}

const TitleAndFavorite: FC<TitleAndFavoriteProps> = ({ className, id, isFavorite, name }) => (
  <div className={cn(className, 'block text-lapel lg:flex mb-1 lg:mb-0')}>
    <h1 className="heading-small flex-grow text-center lg:heading-medium lg:text-left">{name}</h1>
    {typeof isFavorite !== 'undefined' && typeof id !== 'undefined' && (
      <CardFavoriteButton
        className="ml-4 hidden lg:block"
        favorite={isFavorite}
        id={id}
        stroke={colors.lapel}
      />
    )}
  </div>
);

interface PartnerLinkProps {
  className?: string;
  partner: any;
}

const PartnerLink: FC<PartnerLinkProps> = ({ className, partner }) => {
  if (partner && partner.name !== 'Paperless Post' && partner.name !== 'paperless_post') {
    return (
      <AppLink
        className={cn(
          className,
          'block paragraph-xsmall lg:paragraph-small text-center text-dusty-gray mt-1 lg:text-left'
        )}
        href={`/cards/group/${partner.slug || partner.name}`}
        tracking={{ id: 'pdp-partner' }}
      >
        {partner?.meta?.label || partner.name}
      </AppLink>
    );
  }

  return null;
};

interface CustomizeButtonProps {
  className?: string;
  children: any;
  href: any;
  authRequired?: boolean;
  customizeInApp?: boolean;
  tracking?: any;
}

const CustomizeButton: FC<CustomizeButtonProps> = ({
  className,
  children,
  href,
  authRequired = true,
  customizeInApp = false,
  tracking = {},
}) => {
  const LinkComponent = authRequired ? AuthenticatedAppLink : AppLink;

  return (
    <LinkComponent
      className={cn(
        className,
        customizeInApp ? undefined : CustomizeButtonStyles,
        'block w-full py-2.5 paragraph-medium text-center rounded-full',
        {
          'text-white bg-gatsby': !customizeInApp,
          'text-gatsby bg-white': customizeInApp,
        }
      )}
      data-cnstrc-btn="add_to_cart"
      href={href}
      rel="nofollow,noindex"
      tracking={tracking}
    >
      {children}
    </LinkComponent>
  );
};

interface PreviewExperienceButtonProps {
  className?: string;
  setPreviewModalOpen: any;
}

const PreviewExperienceButton: FC<PreviewExperienceButtonProps> = ({
  className,
  setPreviewModalOpen,
}) => (
  <Button
    as="button"
    className={`${className} p-0 w-full`}
    onClick={() => setPreviewModalOpen(true)}
  >
    <span className="flex w-min flex-row items-center paragraph-small text-gatsby border-white hover:border-dark-blue hover:text-dark-blue border-b whitespace-nowrap py-1 pr-2">
      <PlayIcon className="pr-2" height="1.5rem" strokeColor={colors.gatsby} width="1.5rem" />
      Preview experience
    </span>
  </Button>
);

interface DescriptionParagraphProps {
  className?: string;
  description: string;
}

const DescriptionParagraph: FC<DescriptionParagraphProps> = ({ className, description }) => (
  <p className={cn(className, 'paragraph-small text-charcoal')}>{description}</p>
);

interface CoinsStartingAtProps {
  className?: string;
  coinsStartingAt?: number;
  questionClick: any;
}

const CoinsStartingAt: FC<CoinsStartingAtProps> = ({
  className,
  coinsStartingAt = 0,
  questionClick,
}) => (
  <span
    className={cn(className, 'flex paragraph-small text-lapel font-medium items-center align-top')}
  >
    {coinsStartingAt > 0
      ? `Starting at ${coinsStartingAt} Coins`
      : 'Starting at Free up to 50 recipients'}
    <span onClick={questionClick}>
      <QuestionIcon
        className="ml-2 inline-block align-top cursor-pointer"
        height="1.125rem"
        strokeColor={colors.gatsby}
        width="1.125rem"
      />
    </span>
  </span>
);

interface CardFeaturesProps {
  className?: string;
}

const CardFeatures: FC<CardFeaturesProps> = ({ className }) => (
  <div className={`${className}`}>
    <div className="flex items-center my-4 w-full">
      <span className="w-6 h-6 mr-4">
        <PaletteIcon height="1.5rem" width="1.5rem" />
      </span>
      <p className="text-charcoal paragraph-small">
        Change your Card’s text, style, envelope, stamp, backdrop, and more. Add additional text
        boxes where you want them.
      </p>
    </div>
    <div className="flex items-center my-4 w-full">
      <span className="w-6 h-6 mr-4">
        <PencilEditIcon height="1.5rem" width="1.5rem" />
      </span>
      <p className="text-charcoal paragraph-small">
        Personalize with your event’s details, make or import a guest list, and send with a click.
      </p>
    </div>
    <div className="flex items-center my-4 w-full">
      <span className="w-6 h-6 mr-4">
        <BlocksIcon height="1.5rem" width="1.5rem" />
      </span>
      <p className="text-charcoal paragraph-small">
        Use customizable Blocks for schedule, registry, accommodations, travel details, and more.
      </p>
    </div>
    <div className="flex items-center my-4 w-full">
      <span className="w-6 h-6 mr-4">
        <PreviewMobileIcon height="1.5rem" width="1.5rem" />
      </span>
      <p className="text-charcoal paragraph-small">
        Send your way, via email, text message, or a shareable link you can paste and post anywhere.
      </p>
    </div>
    <div className="flex items-center my-4 w-full">
      <span className="w-6 h-6 mr-4">
        <GuestCheckInIcon height="1.5rem" width="1.5rem" />
      </span>
      <p className="text-charcoal paragraph-small">
        Instantly track opens and RSVPs, communicate with invitees, and use the app to check guests
        in on-site.
      </p>
    </div>
  </div>
);

const CoreCardFeatures: FC<CardFeaturesProps> = ({ className }) => (
  <div className={`${className}`}>
    <div className="flex items-center my-4 w-full">
      <span className="w-6 h-6 mr-4">
        <PaletteIcon height="1.5rem" width="1.5rem" />
      </span>
      <p className="text-charcoal paragraph-small">
        Change your card’s text, style, envelope, backdrop and more
      </p>
    </div>
    <div className="flex items-center my-4 w-full">
      <span className="w-6 h-6 mr-4">
        <EnvelopeIcon height="1.5rem" width="1.5rem" />
      </span>
      <p className="text-charcoal paragraph-small">
        Add your event’s details, make or import a guest list, and send with a click
      </p>
    </div>
    <div className="flex items-center my-4 w-full">
      <span className="w-6 h-6 mr-4">
        <TrackingIcon height="1.5rem" width="1.5rem" />
      </span>
      <p className="text-charcoal paragraph-small">
        Track RSVPs and communicate with guests instantly
      </p>
    </div>
  </div>
);

interface FlyerFeaturesProps {
  className?: string;
}

const FlyerFeatures: FC<FlyerFeaturesProps> = ({ className }) => (
  <div className={`${className} border-t border-dove py-4`}>
    <div className="flex items-center my-4 w-full">
      <span className="w-6 h-6 mr-4">
        <NoAdsIcon height="1.5rem" width="1.5rem" />
      </span>
      <p className="text-charcoal paragraph-small">No ads. Ever. For you or for your guests.</p>
    </div>
    <div className="flex items-center my-4 w-full">
      <span className="w-6 h-6 mr-4">
        <PaletteIcon height="1.5rem" width="1.5rem" />
      </span>
      <p className="text-charcoal paragraph-small">
        Bring your invite to life with our curated templates. Get creative with backgrounds,
        stickers, text effects, and colors.
      </p>
    </div>
    <div className="flex items-center my-4 w-full">
      <span className="w-6 h-6 mr-4">
        <BlocksIcon height="1.5rem" width="1.5rem" />
      </span>
      <p className="text-charcoal paragraph-small">
        Add registry, accommodation, or ticketing blocks. Or customize a text block to share
        whatever information you need.
      </p>
    </div>
    <div className="flex items-center my-4 w-full">
      <span className="w-6 h-6 mr-4">
        <TrackingIcon height="1.5rem" width="1.5rem" />
      </span>
      <p className="text-charcoal paragraph-small">
        Need tracking? Send email or text message invites through Paperless Post.
      </p>
    </div>
    <div className="flex items-center my-4 w-full">
      <span className="w-6 h-6 mr-4">
        <ShareIcon height="1.5rem" width="1.5rem" />
      </span>
      <p className="text-charcoal paragraph-small">
        Share anywhere with a link: text, social media, or WhatsApp. Prefer email? Send through
        Paperless Post.
      </p>
    </div>
  </div>
);

const PAPERSOURCE_DEEP_LINK_TEXT = 'Looking for paper?  Shop this printed card at ';
const PAPERSOURCE_SHALLOW_LINK_TEXT =
  'Looking for paper?  Shop our printed collection exclusively at ';

const PaperSourceReference = css`
  & a,
  & a:visited {
    color: ${colors.gatsby};
  }
`;

interface PaperSourceLinkProps {
  id: string;
  type: string;
  url: string;
}

const PaperSourceLink: FC<PaperSourceLinkProps> = ({ type, url }) => {
  if (type === 'papersource_shallow' || type === 'papersource_deep') {
    return (
      <div className={cn('paragraph-small my-3 text-dusty-gray mr-8', PaperSourceReference)}>
        {type === 'papersource_deep' ? PAPERSOURCE_DEEP_LINK_TEXT : PAPERSOURCE_SHALLOW_LINK_TEXT}
        <AppLink href={url} target="_blank" tracking={{ id: 'paper-source-link' }}>
          Paper Source
        </AppLink>
        .
      </div>
    );
  }

  return null;
};

export {
  TitleAndFavorite,
  PartnerLink,
  CustomizeButton,
  PreviewExperienceButton,
  DescriptionParagraph,
  CoinsStartingAt,
  CardFeatures,
  CoreCardFeatures,
  FlyerFeatures,
  PaperSourceLink,
};
